import React, { useCallback, useContext, useState } from "react"

import { AuthContext } from "@izettle/react-spa"

import { config, Environment } from "@/config"
import { DocumentPurposes, Purpose, PurposesState, SellerType } from "@/models"
import { fetchPurposes } from "@/services"

import { PurposesContext } from "./context"

export const PurposesProvider: React.FC = ({ children }) => {
  const [purposes, setPurposes] = useState<Purpose[]>([])
  const [purposeState, setPurposeState] = useState<PurposesState>({ kind: "new" })
  const [sellerType, setSellerType] = useState<SellerType>(SellerType.New)
  const { getToken } = useContext(AuthContext)

  const isTaskCompleted = (p: Purpose[]): boolean =>
    p.length > 0 && p.every((purpose) => purpose.status === "done")

  const isTaskDone = (p: Purpose[]): boolean => p.length === 0

  const getPurposes = useCallback(async (): Promise<Purpose[]> => {
    try {
      setPurposeState({ kind: "loading" })
      const purposesFromApi: DocumentPurposes = await fetchPurposes(getToken)
      setPurposes(purposesFromApi.documentPurposeItems)
      setSellerType(
        purposesFromApi.isExistingSeller ? SellerType.Existing : SellerType.New,
      )
      if (purposesFromApi.isSellerVerificationTaskRejected) {
        setPurposeState({ kind: "rejected" })
      } else if (isTaskCompleted(purposesFromApi.documentPurposeItems)) {
        setPurposeState({ kind: "completed" })
      } else if (isTaskDone(purposesFromApi.documentPurposeItems)) {
        setPurposeState({ kind: "done" })
      } else {
        setPurposeState({ kind: "new" })
      }

      return purposesFromApi.documentPurposeItems
    } catch {
      setPurposeState({ kind: "loading-error" })
      return []
    }
  }, [fetchPurposes])

  const checkValidPurpose = useCallback(
    async (documentPurposeType: string): Promise<boolean> => {
      let currentPurposes: Purpose[] = purposes
      if (purposes.length === 0) {
        currentPurposes = await getPurposes()
      }

      // Bypass the flow validation when running in development environment
      // The bypass in development environment will help developers to jump directly into flows
      // even if the user in use doesn't have that purpose assign or is already completed.
      return config.appEnv === Environment.Development
        ? true
        : currentPurposes.some(
            (p) => p.documentPurposeType === documentPurposeType && p.status === "todo",
          )
    },
    [getPurposes, purposes],
  )

  return (
    <PurposesContext.Provider
      value={{ purposes, getPurposes, purposeState, sellerType, checkValidPurpose }}
    >
      {children}
    </PurposesContext.Provider>
  )
}
